import { FC } from "react";
import { useTranslation } from "react-i18next";
import GoogleLogoSVG from "../../resources/svg/logos/GoogleLogoSVG";
import useCustomFormik from "../../../custom-hooks/useCustomFormik";
import { useNavigate } from "react-router-dom";
import { userLoginFields, userLoginSchemaFactory } from "../../../validation-schemas/user-schemas/UserLoginSchema";
import { Link } from "react-router-dom";
import { useAppDispatch } from "../../../custom-hooks/useAppDispatch";
import { UserServiceImpl } from "../../../services/UserServiceImpl";
import CustomError from "../../../custom-components/form-inputs/CustomError";
import { useSelector } from "react-redux";
import { selectUser } from "../../../slices/user-slice";
import ResetPassword from "./reset-password/ResetPassword";
import { useErrorHandler } from "../../../custom-hooks/base-hooks/useErrorHandler";
import { StatusCodes } from "http-status-codes";
import CustomInput from "../../../custom-components/form-inputs/CustomInput";
import PasswordInput from "../../resources/components/form-inputs/PasswordInput";
import { useGoogleLogin } from "@react-oauth/google";

const LoginForm: FC = () => {
    const user = useSelector(selectUser());
    const { t } = useTranslation();
    const navigate = useNavigate();
    const dispatch = useAppDispatch();

    const {
        isError: wrongCredentials,
        resetErrors: resetForbiddenErrors,
        errorKey: wrongCredentialsKey,
    } = useErrorHandler(StatusCodes.FORBIDDEN, "errors.validation.invalidEmailOrPassword");

    const {
        isError: userHasBeenBlocked,
        resetErrors: resetBlockedErrors,
        errorKey: userHasBeenBlockedKey,
    } = useErrorHandler(StatusCodes.LOCKED, "errors.validation.userHasBeenBlocked");

    const {
        isError: userHasBeenDeleted,
        resetErrors: resetGoneErrors,
        errorKey: userHasBeenDeletedKey,
    } = useErrorHandler(StatusCodes.GONE, "errors.validation.userHasBeenDeleted");

    const { form, errorKey, setErrorKey } = useCustomFormik({
        initial: { email: user.email },
        fields: userLoginFields,
        schema: userLoginSchemaFactory(t),
        onSubmit: async (values) => {
            resetForbiddenErrors();
            resetGoneErrors();
            resetBlockedErrors();
            const user = await dispatch(UserServiceImpl.loginThunk(values));
            if (
                wrongCredentials() ||
                userHasBeenDeleted() ||
                userHasBeenBlocked() ||
                user.meta.requestStatus !== "fulfilled"
            ) {
                setErrorKey("errors.validation.invalidEmailOrPassword");
                return;
            }
            navigate("/");
        },
    });
    const login = useGoogleLogin({
        onSuccess: async (tokenResponse) => {
            const user = await dispatch(UserServiceImpl.googleLoginThunk({ code: tokenResponse.code }));
            if (
                wrongCredentials() ||
                userHasBeenDeleted() ||
                userHasBeenBlocked() ||
                user.meta.requestStatus !== "fulfilled"
            ) {
                setErrorKey("errors.common");
                return;
            }
            navigate("/");
        },
        flow: "auth-code",
    });

    return (
        <div
            className={`relative flex flex-col justify-center items-center ${
                user?.id ? "tmax4:h-[calc(100vh-108px)]" : "tmax4:h-[calc(100vh-68px)]"
            } mb-[80px]`}
        >
            <form onSubmit={form.handleSubmit} className="relative w-[410px] dmax2:w-[340px]">
                <h1 className="H32B dmax2:H24B text-textWhite text-center my-[50px] td4:my-8 tmax4:my-6">
                    {t("login.login")}
                </h1>
                <div
                    onClick={() => login()}
                    className="button H20 dmax2:H16 bg-backgroundButton hover:bg-backgroundInput w-full h-[60px] dmax2:h-[45px] mb-8 dmax2:mb-6 transition-all duration-300"
                >
                    <div className="mb-[2px] dmax2:mb-[1px]">{t("login.loginAlt")}</div>
                    <GoogleLogoSVG className="ml-4 dmax2:w-6 dmax2:h-6" />
                </div>
                <CustomInput
                    form={form}
                    name="email"
                    fields={userLoginFields}
                    className="w-full h-[63px] dmax2:h-[45px] mb-3 dmax2:mb-2"
                />
                <PasswordInput form={form} name="password" fields={userLoginFields} />
                <ResetPassword />
                <CustomError
                    form={form}
                    errorKey={wrongCredentialsKey || userHasBeenDeletedKey || userHasBeenBlockedKey || errorKey}
                    className="mb-4 text-center"
                />
                <button className="button H20 dmax2:H14B w-full h-[60px] dmax2:h-[40px] mb-3 dmax2:mb-2 transition-all duration-300">
                    <div className="mb-[2px] dmax2:mb-[1px]">{t("login.loginAction")}</div>
                </button>
                <Link
                    to="/registration"
                    className="button H20 dmax2:H14B bg-transparent hover:bg-primaryBlueFocused border-2 border-primaryBlueFocused w-full h-[60px] dmax2:h-[40px] transition-all duration-300"
                >
                    <div className="mb-[2px] dmax2:mb-[1px]">{t("registration.registrationAction")}</div>
                </Link>
            </form>
        </div>
    );
};

export default LoginForm;
